const ACTION_ASSOCIATION_WINDOW_MS = 5000;
const MINIMUM_LOADING_MS = 160;
const CLEAR_DELAY_MS = 120;
const UNUSED_RESPONSE_BODY_TIMEOUT_MS = 1000;
const RESPONSE_READ_METHODS = ["arrayBuffer", "blob", "formData", "json", "text"];

const getActionElement = (target) => {
  if (!(target instanceof Element)) return null;
  return target.closest(
    'button, a[href], [role="button"], input[type="submit"], input[type="button"], input[type="file"], label',
  );
};

const getActionLabel = (element) => {
  const label = element.getAttribute("aria-label") ||
    element.getAttribute("title") ||
    element.labels?.[0]?.innerText ||
    element.labels?.[0]?.textContent ||
    element.innerText ||
    element.textContent ||
    element.value ||
    "Action";
  return label.replace(/\s+/g, " ").trim();
};

const wrapResponseReadMethod = (response, method, onBodyRead, onBodyFinished) => {
  const readBody = response[method].bind(response);
  Object.defineProperty(response, method, {
    configurable: true,
    value(...args) {
      onBodyRead();
      let body;
      try {
        body = readBody(...args);
      } catch (error) {
        onBodyFinished();
        throw error;
      }
      return Promise.resolve(body).finally(onBodyFinished);
    },
  });
};

export const installActionProgressTracking = (targetWindow) => {
  const { document } = targetWindow;
  const originalFetch = targetWindow.fetch;
  const actions = new Map();
  let actionCandidate = null;
  let candidateTimer = null;

  const status = document.createElement("div");
  status.className = "bq-action-progress-status";
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  status.setAttribute("aria-atomic", "true");
  status.hidden = true;
  document.body.append(status);

  const updateStatus = () => {
    const activeAction = [...actions.values()]
      .sort((left, right) => right.startedAt - left.startedAt)[0];
    if (!activeAction) {
      status.hidden = true;
      status.textContent = "";
      return;
    }

    status.textContent = `In progress: ${activeAction.label}`;
    status.hidden = false;
  };

  const restoreActionAttributes = (action) => {
    if (action.element.getAttribute("aria-busy") === "true") {
      if (action.previousAriaBusy === null) {
        action.element.removeAttribute("aria-busy");
      } else {
        action.element.setAttribute("aria-busy", action.previousAriaBusy);
      }
    }
    if (action.element.getAttribute("data-bq-action-loading") === "true") {
      if (action.previousLoading === null) {
        action.element.removeAttribute("data-bq-action-loading");
      } else {
        action.element.setAttribute("data-bq-action-loading", action.previousLoading);
      }
    }
  };

  const clearAction = (action) => {
    if (action.timer) targetWindow.clearTimeout(action.timer);
    restoreActionAttributes(action);
    actions.delete(action.element);
    updateStatus();
  };

  const beginAction = (element) => {
    let action = actions.get(element);
    if (!action) {
      action = {
        element,
        label: getActionLabel(element),
        startedAt: Date.now(),
        requestCount: 0,
        timer: null,
        previousAriaBusy: element.getAttribute("aria-busy"),
        previousLoading: element.getAttribute("data-bq-action-loading"),
      };
      actions.set(element, action);
      element.setAttribute("aria-busy", "true");
      element.setAttribute("data-bq-action-loading", "true");
    }
    if (action.timer) {
      targetWindow.clearTimeout(action.timer);
      action.timer = null;
    }
    action.requestCount += 1;
    updateStatus();
    return action;
  };

  const finishActionRequest = (action) => {
    action.requestCount = Math.max(0, action.requestCount - 1);
    if (action.requestCount !== 0) return;

    const remainingMinimum = Math.max(
      0,
      MINIMUM_LOADING_MS - (Date.now() - action.startedAt),
    );
    action.timer = targetWindow.setTimeout(
      () => clearAction(action),
      Math.max(remainingMinimum, CLEAR_DELAY_MS),
    );
  };

  const trackResponseBody = (response, action) => {
    let completed = false;
    let bodyTimer;
    const finishBody = () => {
      if (completed) return;
      completed = true;
      if (bodyTimer) targetWindow.clearTimeout(bodyTimer);
      finishActionRequest(action);
    };
    const onBodyRead = () => {
      if (bodyTimer) targetWindow.clearTimeout(bodyTimer);
    };

    for (const method of RESPONSE_READ_METHODS) {
      if (typeof response[method] !== "function") continue;
      wrapResponseReadMethod(response, method, onBodyRead, finishBody);
    }

    bodyTimer = targetWindow.setTimeout(finishBody, UNUSED_RESPONSE_BODY_TIMEOUT_MS);
  };

  const setCandidate = (element) => {
    if (candidateTimer) targetWindow.clearTimeout(candidateTimer);
    if (!element) {
      actionCandidate = null;
      return;
    }
    actionCandidate = { element, clickedAt: Date.now() };
    candidateTimer = targetWindow.setTimeout(() => {
      actionCandidate = null;
      candidateTimer = null;
    }, ACTION_ASSOCIATION_WINDOW_MS);
  };

  const onClick = (event) => {
    const element = getActionElement(event.target);
    if (element?.getAttribute("data-bq-action-loading") === "true") {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    setCandidate(element?.closest('[data-bq-progress-managed="true"]') ? null : element);
  };

  const onSubmit = (event) => {
    const submitter = event.submitter ||
      event.target.querySelector('button[type="submit"], input[type="submit"]');
    setCandidate(submitter || null);
  };

  document.addEventListener("click", onClick, true);
  document.addEventListener("submit", onSubmit, true);

  const trackedFetch = function (...args) {
    const candidate = actionCandidate;
    const action = candidate?.element?.isConnected &&
      Date.now() - (candidate.clickedAt ?? candidate.startedAt) <= ACTION_ASSOCIATION_WINDOW_MS
      ? beginAction(candidate.element)
      : null;

    let request;
    try {
      request = originalFetch.apply(targetWindow, args);
    } catch (error) {
      if (action) finishActionRequest(action);
      throw error;
    }

    if (!action) return request;
    return Promise.resolve(request).then(
      (response) => {
        trackResponseBody(response, action);
        return response;
      },
      (error) => {
        finishActionRequest(action);
        throw error;
      },
    );
  };

  targetWindow.fetch = trackedFetch;

  return () => {
    document.removeEventListener("click", onClick, true);
    document.removeEventListener("submit", onSubmit, true);
    if (candidateTimer) targetWindow.clearTimeout(candidateTimer);
    for (const action of actions.values()) {
      if (action.timer) targetWindow.clearTimeout(action.timer);
      restoreActionAttributes(action);
    }
    actions.clear();
    status.remove();
    if (targetWindow.fetch === trackedFetch) targetWindow.fetch = originalFetch;
  };
};
